import { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    APIGetMapsDetails,
    APIGetMapsDetailsData,
    APIGetMapsDetailsIntegration,
    APIPutMapsName,
} from '../api/maps/Maps';
import mapboxgl from 'mapbox-gl';
import {getMap} from './utils/mapboxConfig';
import { getMapOverviewData } from './utils/mapOverviewHandler';
import {ButtonLegal, ButtonPlanPicker} from './components/mapButtons';
import LegalSideOver from './components/legalSideOver';
import { ViewerContext } from '../context/ViewerContext';
import { HiChevronLeft } from 'react-icons/hi';
import { MdOutlineEdit, MdOutlineSave } from 'react-icons/md';
import PlanToMapPicker from './components/planToMapPicker';
import { UserContext } from '../context/UserContext';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

export const ViewerMapOverview = () => {
    const {mapId, integrationAccessId} = useParams();

    const { user } = useContext(UserContext);
    const mapContainerRef = useRef(null);
    const [map, setMap] = useState(null);
    const [data, setData] = useState(null);
    const [mapDetails, setMapDetails] = useState(null);
    const [showLegal, setShowLegal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState(false);
    const [showPicker, setShowPicker] = useState(false);

    const navigate = useNavigate();

    const handelMapLoad = () => {
        
        const { bbox, mapPlots, mapPolygons } = getMapOverviewData(data);
        console.log(mapPlots, mapPolygons);
        const zoomThreshold = 12;
        const map = getMap(mapContainerRef);
        map.addControl(new ButtonLegal(() => setShowLegal(true)), 'bottom-right');
        // map.addControl(new mapboxgl.NavigationControl(), 'top-right');

        if (mapId) {
            map.addControl(new ButtonPlanPicker(() => setShowPicker(true)));
        }

        map.on('load', () => {
            if (!map.getSource("xplans")) {
                map.addSource('xplans', {
                    type: 'geojson',
                    cluster: true,
                    clusterMaxZoom: zoomThreshold,
                    clusterRadius: 50,
                    data: {
                        type: 'FeatureCollection',
                        name: 'xplans',
                        features: mapPlots,
                    },
                });
            }
            if (!map.getSource("xplansPolygons")) {
                map.addSource('xplansPolygons', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        name: 'xplansPolygons',
                        features: mapPolygons,
                    },
                });
            }
            map.addLayer({
                id: 'xplans-cycle',
                type: 'circle',
                maxzoom: zoomThreshold,
                source: 'xplans',
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': [
                        'step',
                        ['get', 'point_count'],
                        '#111111',
                        100,
                        '#444444',
                        700,
                        '#888888',
                    ],
                    'circle-radius': [
                        'step',
                        ['get', 'point_count'],
                        20,
                        100,
                        30,
                        750,
                        40,
                    ],
                },
            });
            map.addLayer({
                id: 'xplans-count',
                type: 'symbol',
                maxzoom: zoomThreshold,
                source: 'xplans',
                filter: ['has', 'point_count'],
                paint: {
                    'text-color': '#ffffff',
                },
                layout: {
                    'text-field': ['get', 'point_count_abbreviated'],
                    'text-font': [
                        'DIN Offc Pro Medium',
                        'Arial Unicode MS Bold',
                    ],
                    'text-size': 12,
                },
            });
            map.addLayer({
                id: 'xplans-unclustered',
                type: 'circle',
                maxzoom: zoomThreshold,
                source: 'xplans',
                filter: ['!', ['has', 'point_count']],
                paint: {
                    'circle-color': '#455560',
                    'circle-radius': 10,
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#fff',
                },
            });
            map.addLayer({
                id: 'outline-polygon',
                type: 'line',
                minzoom: zoomThreshold,
                filter: ['!', ['has', 'point_count']],
                source: 'xplansPolygons',
                paint: {
                    'line-color': '#455560',
                    'line-width': 3,
                },
            });
            // add another layer with the names of the polygons
            map.addLayer({
                id: 'xplans-names',
                type: 'symbol',
                minzoom: zoomThreshold,
                filter: ['!', ['has', 'point_count']],
                source: 'xplansPolygons',
                layout: {
                    'text-field': ['get', 'name'],
                    'text-font': [
                        'DIN Offc Pro Medium',
                        'Arial Unicode MS Bold',
                    ],
                    'text-size': 14,
                    'text-anchor': 'center',
                    'text-offset': [0, 0.5],
                },
                paint: {
                    'text-color': '#333',
                    'text-halo-color': '#fff',
                    'text-halo-width': 1,
                },
            });
            map.addLayer({
                id: 'outline-polygon1',
                type: 'fill',
                minzoom: zoomThreshold,
                filter: ['!', ['has', 'point_count']],
                source: 'xplansPolygons',
                paint: {
                    'fill-color': [
                        'match',
                        ['get', 'category'], // property name
                        'xplan',
                        '#007dfe',
                        'outline',
                        '#455560',
                        '#455560',
                    ],
                    'fill-opacity': [
                        'case',
                        ['boolean', ['feature-state', 'hover'], false],
                        0.7,
                        0.2,
                    ],
                },
            });
            map.on('click', function (e) {
                const features = map.queryRenderedFeatures(e.point);

                features.forEach(function (feature) {
                    if (
                        feature.geometry.type === 'Polygon' &&
                        (feature?.layer?.id.substring(0, 5) === 'xplan' ||
                            feature?.layer?.id.substring(0, 15) ===
                                'outline-polygon')
                    ) {
                        if (!mapId) {
                            console.log(`/integration/maps/${integrationAccessId}/${feature?.properties.id}`)
                            navigate( `/integration/maps/${integrationAccessId}/${feature?.properties.id}`, { state: { companyId: feature?.properties.createdByCompany } });
                        } else {
                            window.open(
                                `/viewer/${mapId}/${feature?.properties.id}`,
                                '_self'
                            );
                        }
                    }
                });
            });

            if (bbox.ltLon > 0 && bbox.rbLat > 0) {
                map.fitBounds(
                    [
                        [bbox.ltLat, bbox.ltLon],
                        [bbox.rbLat, bbox.rbLon],
                    ],
                    {
                        padding: { top: 100, bottom: 50, left: 50, right: 50 },
                        duration: 0,
                    }
                );
            }
        });

        setMap(map);
    };

    useEffect(() => {
        if (data !== null) {
            return;
        }
        if (mapId) {
            APIGetMapsDetails(mapId, user?.companyId).then((result) => {
                console.log(result);
                setData(result);
            });
            APIGetMapsDetailsData(mapId, user?.companyId).then(function (result) {
                setMapDetails(result);
                setNewName(result?.name);
            });
        } else {
            APIGetMapsDetailsIntegration(integrationAccessId).then((result) => {
                setData(result);
            });
        }
    }, [integrationAccessId, data, mapId, user?.companyId]);

    if (data !== null && map === null && mapContainerRef !== null) {
        handelMapLoad();
    }

    const handleNameClick = () => {
        setIsEditing(true);
    };

    const handleNameChange = (e) => {
        setNewName(e.target.value);
    };

    const handleNameBlur = () => {
        APIPutMapsName(mapId, newName, user?.companyId).then((result) => {
            setIsEditing(false);
        });
    };

    return (
        <div>
            <div className={ 'text-base z-10 absolute m-5 ml-0 sm:ml-8 mt-6 sm:mt-8 mb-16 leading-7 text-gray-600'}>
                <div className='mt-2  md:flex md:items-center md:justify-between'>
                    <div
                        className='min-w-0 overflow-hidden flex-1'
                        style={{ width: 'calc(100vw - 200px)' }}
                    >
                        {mapId ? (
                            <div className={'flex'}>
                                <a href={'/maps'}>
                                    <HiChevronLeft
                                        className={
                                            'text-3xl h-12 w-12 cursor-pointer mr-2'
                                        }
                                        style={{
                                            strokeWidth: '1px',
                                            fill: '#000',
                                            stroke: '#fff',
                                        }}
                                    />
                                </a>
                                <div>
                                    {isEditing ? (
                                        <div className='flex flex-row'>
                                            <input
                                                type='text'
                                                value={newName}
                                                onChange={handleNameChange}
                                                onBlur={handleNameBlur}
                                                className='text-gray-900 flex-1 text-2xl font-bold border-0 border-transparent bg-[transparent]  rounded focus:ring-0 h-7 pl-0 active:outline-0'
                                                autoFocus
                                            />
                                            <MdOutlineSave
                                                className='ml-4 h-6 w-6 cursor-pointer text-gray-900 self-center'
                                                onClick={handleNameBlur}
                                            />
                                        </div>
                                    ) : (
                                        <div className='flex flex-row w-[calc(100vw-700px)] truncate'>
                                            <h2 className='text-2xl items-center textShadow flex font-normal leading-7 text-gray-900 truncate cursor-default overflow-[hidden] max-w-[95%]'>
                                                {newName}
                                            </h2>
                                            <MdOutlineEdit
                                                className='ml-4  h-6 w-6 cursor-pointer text-gray-900'
                                                onClick={handleNameClick}
                                            />
                                        </div>
                                    )}
                                    <p className=' items-center textShadow flex font-normal leading-7 text-gray-900 truncate mr-12 '>
                                        Zugewiesene Pläne{' '}
                                        {typeof mapDetails?.plans !==
                                        'undefined'
                                            ? mapDetails?.plans?.length
                                            : 0}
                                    </p>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            <ViewerContext.Provider value={{ showLegal, setShowLegal }}>
                <div className={'flex'}>
                    <div
                        className='map-container z-0' ref={mapContainerRef} style={{
                            width: `100%`,
                            height: '100vh',
                        }}
                    ></div>
                    {showPicker ? (
                        <PlanToMapPicker
                            mapId={mapId}
                            mapPlans={data.map((plan) => plan.id)}
                            showPicker={showPicker}
                            setShowPicker={setShowPicker}
                        />
                    ) : null}
                </div>
                <LegalSideOver />
            </ViewerContext.Provider>
        </div>
    );
}
